import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ManageRefereesPanel from "../components/ManageRefereesPanel";
import {
  useGetProbationerApplicationDetailsQuery,
  ProbationerApplication,
} from "../api/probationerApplicationApi";
import { fileUrl } from "../utils/files";
import { STATUS_BLURBS, statusLabel } from "../utils/applicationStatus";
import { STORAGE_KEY } from "../../../features/api/apiSlice";
import {
  ArrowLeft,
  RefreshCw,
  User,
  FileText,
  ShieldCheck,
  MessageSquare,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  GraduationCap,
  Briefcase,
  Award,
  BookOpen,
  Users,
  Building2,
  Hash,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
const STORAGE_BASE = "https://api.niqsng.org/storage";

function toStorageUrl(path?: string | null): string | null {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const fromEnv = fileUrl(path);
  if (fromEnv) return fromEnv;
  return `${STORAGE_BASE}/${path.replace(/^\//, "")}`;
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { dateStyle: "medium" });
}

function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "An error occurred",
  );
}

function getAuth(): any {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  acknowledged: "bg-violet-50 text-violet-700 border-violet-200",
  awaiting_endorsements: "bg-violet-50 text-violet-700 border-violet-200",
  under_review: "bg-blue-50 text-blue-700 border-blue-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  with_committee: "bg-purple-50 text-purple-700 border-purple-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  returned: "bg-amber-50 text-amber-700 border-amber-200",
};

/* ------------------------------------------------------------------ */
/* Atoms                                                               */
/* ------------------------------------------------------------------ */
function Row({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value?: any;
  icon?: any;
}) {
  const val =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-gray-50 last:border-0 gap-4 text-sm">
      <div className="text-gray-400 flex items-center gap-1.5 shrink-0 min-w-[130px]">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="font-medium">{label}</span>
      </div>
      <div className="text-gray-900 font-semibold text-right">{val}</div>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
  badge,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  badge?: number;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-[24px] shadow-sm overflow-hidden">
      <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">
            {title}
          </h3>
        </div>
        {badge !== undefined && badge > 0 && (
          <span className="text-[10px] font-black bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="py-6 text-center text-xs font-bold text-gray-300 uppercase tracking-widest italic">
      {label}
    </div>
  );
}

function FileButton({ label, url }: { label: string; url: string | null }) {
  if (!url)
    return (
      <div className="p-4 bg-gray-50 border border-gray-100 rounded-2xl opacity-50">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          {label}
        </p>
        <p className="text-xs text-gray-400 italic mt-1 font-medium">
          Not Uploaded
        </p>
      </div>
    );
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="p-4 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all group flex items-center justify-between"
    >
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
          {label}
        </p>
        <p className="text-xs text-gray-900 mt-1 font-bold">View Document</p>
      </div>
      <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-gray-900 group-hover:text-white transition-all">
        <Eye className="w-4 h-4" />
      </div>
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function ApplicationDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch, isFetching } =
    useGetProbationerApplicationDetailsQuery(id as string, { skip: !id });

  const app = (data ?? null) as
    | (ProbationerApplication & Record<string, any>)
    | null;
  console.log("this is the full applicant", app);
  const errMsg = error ? safeMsg(error) : null;

  // Referees can be swapped while their acceptance is still what the
  // application is waiting on — not once it has moved past that rung.
  const canEditReferees =
    app?.status === "pending" ||
    app?.status === "acknowledged" ||
    app?.status === "awaiting_endorsements" ||
    app?.status === "returned";

  const auth = getAuth();
  const myMemberId = auth?.user?.member?.id || auth?.member_id || null;
  const isReviewer =
    !!myMemberId &&
    !!app?.reviewer_id &&
    String(app.reviewer_id) === String(myMemberId);
  // The assigned reviewer recommends from their committee page; the chairman
  // finalises. Deciding here put the outcome in one person's hands. Shown only
  // once the application is actually WITH the committee — before that there is
  // nothing for its reviewer to say.
  const canReviewerDecide =
    isReviewer && String(app?.status) === "with_committee";

  /* derived collections — normalise both snake/camelCase variants */
  const qualifications = Array.isArray(app?.qualifications)
    ? app!.qualifications
    : [];
  const oLevel = Array.isArray(app?.o_level_results)
    ? app!.o_level_results
    : [];
  const memberships = Array.isArray(
    app?.professional_memberships ?? app?.professionalMemberships,
  )
    ? (app!.professional_memberships ?? app!.professionalMemberships)!
    : [];
  const experiences = Array.isArray(
    app?.work_experiences ?? app?.workExperiences,
  )
    ? (app!.work_experiences ?? app!.workExperiences)!
    : [];
  const seminars = Array.isArray(app?.seminars) ? app!.seminars : [];
  const reviews = Array.isArray(app?.reviews) ? app!.reviews : [];
  const referees = Array.isArray(app?.referees) ? app!.referees : [];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8 pb-20">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-2">
          <nav className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-tighter">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-900 transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
            <span className="opacity-30">/</span>
            <Link
              to="/probationer-applications"
              className="hover:text-gray-900 transition-colors"
            >
              Applications
            </Link>
            <span className="opacity-30">/</span>
            <span className="text-gray-900">Details</span>
          </nav>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Application Details
          </h1>
          {app && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded">
                ID: {id}
              </span>
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${
                  STATUS_STYLES[app.status] ??
                  "bg-gray-50 text-gray-700 border-gray-100"
                }`}
              >
                {statusLabel(app.status)}
              </span>
              {app.completion_step != null && (
                <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  Step {app.completion_step} of 8
                </span>
              )}
            </div>
          )}
          {/* Where it actually stands. The badge names the rung; this says what
              is happening on it, so an applicant is never left reading a status
              word and guessing who is holding their application. */}
          {app && STATUS_BLURBS[String(app.status)] && (
            <p className="text-sm text-gray-600">{STATUS_BLURBS[String(app.status)]}</p>
          )}
        </div>

        <button
          onClick={() => refetch()}
          disabled={isLoading || isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
          />
          {isFetching ? "Syncing..." : "Refresh"}
        </button>
      </div>

      {/* ── States ── */}
      {isLoading && (
        <div className="h-64 flex flex-col items-center justify-center text-gray-400 gap-4">
          <Clock className="w-10 h-10 animate-pulse" />
          <p className="font-bold text-sm uppercase tracking-widest">
            Loading application…
          </p>
        </div>
      )}

      {errMsg && !isLoading && (
        <div className="p-6 bg-red-50 border border-red-100 rounded-3xl flex items-center gap-4 text-red-700">
          <AlertCircle className="w-6 h-6 shrink-0" />
          <p className="font-bold">{errMsg}</p>
        </div>
      )}

      {/* ── Content ── */}
      {!isLoading && !errMsg && app && (
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* ════════════ LEFT (main content) ════════════ */}
          <div className="lg:col-span-8 space-y-6">
            {/* 1. Personal Info */}
            <Section title="Personal Information" icon={User}>
              <div className="grid md:grid-cols-2 gap-x-10">
                <div>
                  <Row label="Title" value={app.title} />
                  <Row label="Surname" value={app.surname} />
                  <Row label="Other Names" value={app.other_names} />
                  <Row label="Gender" value={app.gender} />
                  <Row
                    label="Date of Birth"
                    value={fmtDate(app.date_of_birth)}
                    icon={Calendar}
                  />
                  <Row label="Nationality" value={app.nationality} />
                </div>
                <div>
                  <Row label="Email" value={app.email} icon={Mail} />
                  <Row label="Phone" value={app.phone} icon={Phone} />
                  <Row
                    label="Category"
                    value={app.category?.name ?? app.application_category_id}
                  />
                  <Row
                    label="Membership No"
                    value={app.membership_no}
                    icon={Hash}
                  />
                  <Row
                    label="Membership Grade"
                    value={app.membership_grade}
                    icon={Award}
                  />
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-50 grid md:grid-cols-2 gap-4">
                <FileButton
                  label="Passport Photo"
                  url={toStorageUrl(app.passport_photo ?? app.pro_pic)}
                />
                <FileButton
                  label="Applicant Signature"
                  url={toStorageUrl(app.signature)}
                />
              </div>
            </Section>

            {/* 2. Location */}
            <Section title="Location & Address" icon={MapPin}>
              <div className="grid md:grid-cols-2 gap-x-10">
                <div>
                  <Row
                    label="State of Residence"
                    value={app.state?.name ?? app.state_id}
                  />
                  <Row label="LGA" value={app.lga?.name ?? app.lga_id} />
                  <Row
                    label="Chapter"
                    value={
                      app.chapter?.name ?? app.chapter_other ?? app.chapter_id
                    }
                    icon={Building2}
                  />
                  <Row
                    label="Sector"
                    value={app.sector?.name ?? app.sector_id}
                  />
                </div>
                <div>
                  <Row
                    label="Residential Address"
                    value={app.residential_address}
                    icon={MapPin}
                  />
                  <Row label="Postal Address" value={app.postal_address} />
                </div>
              </div>
            </Section>

            {/* 3. Academic Qualifications */}
            <Section
              title="Academic Qualifications"
              icon={GraduationCap}
              badge={qualifications.length}
            >
              {qualifications.length === 0 ? (
                <EmptyState label="No qualifications recorded" />
              ) : (
                <div className="space-y-4">
                  {qualifications.map((q: any, idx: number) => (
                    <div
                      key={q.id ?? idx}
                      className="p-4 bg-gray-50 rounded-2xl flex items-start justify-between gap-4"
                    >
                      <div className="space-y-0.5">
                        <p className="text-sm font-bold text-gray-900">
                          {q.qualification || "—"}
                        </p>
                        <p className="text-xs text-gray-500 font-medium">
                          {q.institution || "—"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {q.year ? `Year: ${q.year}` : ""}
                        </p>
                      </div>
                      <FileButton
                        label="Certificate"
                        url={toStorageUrl(
                          q.cert_file ??
                            q.cert_url ??
                            q.certificate ??
                            q.certificate_url,
                        )}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 4. O-Level Results */}
            {oLevel.length > 0 && (
              <Section
                title="O-Level Results"
                icon={FileText}
                badge={oLevel.length}
              >
                <div className="space-y-4">
                  {oLevel.map((r: any, idx: number) => (
                    <div
                      key={r.id ?? idx}
                      className="p-4 bg-gray-50 rounded-2xl"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-black uppercase tracking-wider text-gray-700">
                          {String(r.exam_type || "").toUpperCase()} —{" "}
                          {r.exam_year || ""}
                        </p>
                        {r.exam_number && (
                          <p className="text-xs text-gray-400 font-medium">
                            Reg No: {r.exam_number}
                          </p>
                        )}
                      </div>
                      {r.subjects && typeof r.subjects === "object" && (
                        <div className="grid grid-cols-2 gap-1">
                          {Object.entries(r.subjects).map(([subj, grade]) => (
                            <div
                              key={subj}
                              className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-1.5 border border-gray-100"
                            >
                              <span className="text-gray-600 font-medium">
                                {subj}
                              </span>
                              <span className="font-black text-gray-900">
                                {String(grade)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {toStorageUrl(r.result_file) && (
                        <a
                          href={toStorageUrl(r.result_file)!}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block mt-3 text-xs font-bold text-indigo-600 hover:underline"
                        >
                          View result scan
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* 5. Professional Memberships */}
            <Section
              title="Professional Memberships"
              icon={Award}
              badge={memberships.length}
            >
              {memberships.length === 0 ? (
                <EmptyState label="No professional memberships recorded" />
              ) : (
                <div className="space-y-3">
                  {memberships.map((m: any, idx: number) => (
                    <div
                      key={m.id ?? idx}
                      className="p-4 bg-gray-50 rounded-2xl flex items-start justify-between gap-4"
                    >
                      <div className="space-y-0.5">
                        <p className="text-sm font-bold text-gray-900">
                          {m.body_name || "—"}
                        </p>
                        {m.stage_passed && (
                          <p className="text-xs text-gray-500 font-medium">
                            Stage: {m.stage_passed}
                          </p>
                        )}
                      </div>
                      <FileButton
                        label="Certificate"
                        url={toStorageUrl(
                          m.certificate_path ??
                            m.certificate_url ??
                            m.certificate,
                        )}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 6. Work Experience */}
            <Section
              title="Work Experience"
              icon={Briefcase}
              badge={experiences.length}
            >
              {experiences.length === 0 ? (
                <EmptyState label="No work experience recorded" />
              ) : (
                <div className="space-y-4">
                  {experiences.map((x: any, idx: number) => (
                    <div
                      key={x.id ?? idx}
                      className="p-4 bg-gray-50 rounded-2xl space-y-2"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-bold text-gray-900">
                            {x.organization || "—"}
                          </p>
                          <p className="text-xs text-gray-500 font-semibold">
                            {x.position || "—"}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 font-medium shrink-0">
                          {fmtDate(x.start_date)} —{" "}
                          {x.end_date ? fmtDate(x.end_date) : "Present"}
                        </p>
                      </div>
                      {x.responsibilities && (
                        <p className="text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-2">
                          {x.responsibilities}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 7. Seminars & CPD */}
            <Section
              title="Seminars & CPD"
              icon={BookOpen}
              badge={seminars.length}
            >
              {seminars.length === 0 ? (
                <EmptyState label="No seminars recorded" />
              ) : (
                <div className="space-y-3">
                  {seminars.map((s: any, idx: number) => (
                    <div
                      key={s.id ?? idx}
                      className="p-4 bg-gray-50 rounded-2xl flex items-center justify-between gap-4"
                    >
                      <div className="space-y-0.5">
                        <p className="text-sm font-bold text-gray-900">
                          {s.title || "—"}
                        </p>
                        {s.location && (
                          <p className="text-xs text-gray-500 font-medium flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {s.location}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 font-medium shrink-0">
                        {fmtDate(s.date)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* ════════════ RIGHT (sidebar) ════════════ */}
          <div className="lg:col-span-4 space-y-6">
            {/* Decision Center */}
            <Section title="Decision Center" icon={ShieldCheck}>
              <div className="space-y-1 mb-4">
                <Row
                  label="Created"
                  value={fmtDate(app.created_at)}
                  icon={Calendar}
                />
                <Row label="Last Updated" value={fmtDate(app.updated_at)} />
                <Row
                  label="Diet"
                  value={app.diet?.title ?? app.logbook_diet_id}
                />
                {app.approved_at && (
                  <Row label="Approved On" value={fmtDate(app.approved_at)} />
                )}
                {app.rejected_at && (
                  <Row label="Rejected On" value={fmtDate(app.rejected_at)} />
                )}
                {app.rejected_reason && (
                  <Row label="Rejection Reason" value={app.rejected_reason} />
                )}
              </div>

              {canReviewerDecide && (
                <div className="pt-4 border-t border-gray-100">
                  <Link
                    to="/committee/entry-reviews"
                    className="flex items-center justify-center gap-2 py-3 bg-gray-900 text-white rounded-xl font-bold text-xs hover:bg-gray-800 transition-all"
                  >
                    <CheckCircle className="w-4 h-4" /> Record your recommendation
                  </Link>
                  <p className="mt-2 text-[10px] text-gray-400 italic text-center leading-tight">
                    This application is assigned to you. Your recommendation goes to the
                    committee chairman, who finalises it.
                  </p>
                </div>
              )}
            </Section>

            {/* Referees */}
            <div className="bg-white border border-gray-200 rounded-[24px] overflow-hidden shadow-sm">
              <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">
                  Referees
                </h3>
                {referees.length > 0 && (
                  <span className="ml-auto text-[10px] font-black bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {referees.length}
                  </span>
                )}
              </div>
              <ManageRefereesPanel
                applicationId={String(app.id)}
                referees={referees}
                canEdit={canEditReferees}
                onDone={refetch}
              />
            </div>

            {/* Review History */}
            <Section
              title="Review History"
              icon={MessageSquare}
              badge={reviews.length}
            >
              {reviews.length === 0 ? (
                <EmptyState label="No remarks logged" />
              ) : (
                <div className="space-y-3">
                  {reviews.map((rev: any) => (
                    <div
                      key={rev.id}
                      className="p-4 bg-gray-50 rounded-2xl space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-gray-400">
                          {fmtDate(rev.created_at)}
                        </span>
                        {rev.status && (
                          <span className="text-[10px] font-black uppercase text-gray-500">
                            {rev.status}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 leading-snug">
                        {rev.comment || rev.remarks || "—"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
